import type { AvatarLicense } from '../stage/AvatarLoader';
import type { DriverConfig, DriverDebug } from '../driver/AvatarDriver';
import type { HandRigConfig } from '../driver/handRig';
import type { SceneManager } from '../scene/SceneManager';
import type { SceneItem } from '../scene/sceneTypes';
import type { Framing } from '../stage/Stage';

export type TrackingState = 'off' | 'starting' | 'on';

type NumericKey = 'headGain' | 'neckShare' | 'pitchOffset' | 'yawOffset' | 'rollOffset';
type BooleanKey = 'mirror' | 'invertPitch' | 'invertYaw' | 'invertRoll' | 'brows' | 'gaze';

export interface PanelCallbacks {
  onPickFile(): void;
  onLoadFixture(path: string): void;
  onToggleTracking(): void;
  onConfigChange(patch: Partial<DriverConfig>): void;
  onFramingChange(framing: Framing): void;
  onCopyObsUrl(): void;
  onCalibrate(): void;
  onResetCalibration(): void;
  onCreditVisibilityChange(visible: boolean): void;
  onSourceChange(kind: string): void;
  onHandConfigChange(patch: Partial<HandRigConfig>): void;
  onResetView(): void;
  onEmotion(name: string): void;
  onCopySceneLink(): void;
}

/** Preset emotions, in hotkey order (1–6). */
export const EMOTIONS: [string, string][] = [
  ['neutral', '무표정'],
  ['happy', '기쁨'],
  ['angry', '화남'],
  ['sad', '슬픔'],
  ['relaxed', '편안'],
  ['surprised', '놀람'],
];

export interface PanelInitialState {
  creditVisible: boolean;
  sourceKind: string;
}

export class Panel {
  private readonly dropzone: HTMLElement;
  private readonly licenseBox: HTMLElement;
  private readonly errorBox: HTMLElement;
  private readonly noticeBox: HTMLElement;
  private readonly trackButton: HTMLButtonElement;
  private readonly statusDot: HTMLElement;
  private readonly statusText: HTMLElement;
  private readonly debugCells = new Map<string, HTMLElement>();
  private readonly emotionChips = new Map<string, HTMLButtonElement>();
  private inspector: HTMLElement | null = null;
  private bgColorInput: HTMLInputElement | null = null;
  private readonly sliders = new Map<NumericKey, { input: HTMLInputElement; readout: HTMLElement }>();

  constructor(
    root: HTMLElement,
    private readonly config: DriverConfig,
    private readonly callbacks: PanelCallbacks,
    private readonly handConfig: HandRigConfig,
    private readonly sceneApi: SceneManager,
    initial: PanelInitialState,
  ) {
    root.innerHTML = '';

    this.dropzone = el('div', 'dropzone', [
      el('div', 'big', ['VRM을 여기에 놓으세요']),
      el('div', 'sub', [
        '업로드되지 않습니다 — 파일은 브라우저 안에서만 열립니다. ',
        'VRM 0.x와 1.0 모두 지원합니다.',
      ]),
      (() => {
        const actions = el('div', 'actions');
        actions.append(button('파일 선택', 'primary', () => this.callbacks.onPickFile()));
        // The fixture models are licensed author-only / no-redistribution, so
        // they exist ONLY in local dev — the deploy strips them from dist and
        // these buttons follow.
        if (import.meta.env.DEV) {
          actions.append(
            button('tttt.vrm (1.0)', '', () => this.callbacks.onLoadFixture('/fixtures/tttt.vrm')),
            button('reee.vrm (0.x)', '', () => this.callbacks.onLoadFixture('/fixtures/reee.vrm')),
          );
        }
        return actions;
      })(),
    ]);

    this.statusDot = el('span', 'dot');
    this.statusText = el('span', '', ['정지']);
    this.trackButton = button('트래킹 시작', 'primary wide', () =>
      this.callbacks.onToggleTracking(),
    );

    this.licenseBox = el('div', 'license');

    this.errorBox = el('div', 'error hidden');
    this.noticeBox = el('p', 'hint');

    const panel = el('div', 'panel', [
      el('h1', '', ['VRM Stage']),
      el('p', 'tagline', ['아바타가 들어있는 방송 화면']),

      this.section('아바타', [
        row([button('VRM 열기', 'wide', () => this.callbacks.onPickFile())]),
        row([
          label('프레이밍'),
          select(
            [
              ['bust', '흉상'],
              ['head', '얼굴'],
              ['full', '전신'],
            ],
            'bust',
            (value) => this.callbacks.onFramingChange(value as Framing),
          ),
        ]),
        row([button('시야 초기화', 'wide', () => this.callbacks.onResetView())]),
        el('p', 'hint', ['Ctrl + 드래그로 위치 이동, 휠로 확대·축소.']),
      ]),

      this.section('트래킹', [
        row([
          label('입력'),
          select(
            [
              ['webcam', '웹캠'],
              ['vmc', 'VMC'],
            ],
            initial.sourceKind,
            (value) => this.callbacks.onSourceChange(value),
          ),
        ]),
        row([this.trackButton]),
        row([
          label('상태'),
          (() => {
            const status = el('span', 'status');
            status.append(this.statusDot, this.statusText);
            return status;
          })(),
        ]),
        this.toggle('좌우 미러', 'mirror'),
        this.toggle('눈썹 구동 (BRW 모프)', 'brows'),
        this.toggle('시선 추적', 'gaze'),
      ]),

      this.section('감정 (단축키)', [
        (() => {
          const chips = el('div', 'chips');
          EMOTIONS.forEach(([name, text], index) => {
            const chip = button(`${index + 1} ${text}`, '', () => this.callbacks.onEmotion(name));
            chip.dataset['emotion'] = name;
            chips.append(chip);
            this.emotionChips.set(name, chip);
          });
          return chips;
        })(),
        el('p', 'hint', [
          '숫자키 1–6으로 토글, 0 또는 Esc로 해제. 같은 키를 다시 누르면 풀립니다. ' +
            '열려 있는 OBS 창에도 실시간 반영됩니다.',
        ]),
      ]),

      this.section('손 (웹캠)', [
        this.handToggle('손가락 트래킹', 'fingers'),
        this.handToggle('팔이 손을 따라감', 'arms'),
        this.handToggle('손목 회전', 'wrist'),
        this.handSlider('팔 이동 범위', 'armReach', 0.4, 2, 0.05),
        this.handToggle('손가락 굽힘 반전', 'invertCurl'),
        el('p', 'hint', [
          '팔은 폴 벡터 2본 IK — 팔꿈치는 항상 아래·바깥쪽 평면에서 접히므로 ' +
            '어깨가 등 뒤로 꺾일 수 없습니다. 손목이 이상하게 돌아가면 손목 회전을 끄세요. ' +
            'VMC 입력일 때는 보내는 쪽 리그를 쓰므로 이 항목은 적용되지 않습니다.',
        ]),
      ]),

      this.section('중립 자세', [
        row([button('지금 자세를 정면으로', 'primary wide', () => this.callbacks.onCalibrate())]),
        row([button('보정 초기화', 'wide', () => this.callbacks.onResetCalibration())]),
        this.slider('Pitch 미세조정', 'pitchOffset', -35, 35, 0.5, 'deg'),
        this.slider('Yaw 미세조정', 'yawOffset', -35, 35, 0.5, 'deg'),
        this.noticeBox,
        el('p', 'hint', [
          '웹캠이 눈높이보다 낮으면 얼굴을 아래에서 올려다보게 되어 고개가 계속 들립니다. ' +
            '앉은키가 클수록 심해집니다. 편한 자세로 정면을 본 뒤 위 버튼을 누르면 그 자세가 0이 됩니다.',
        ]),
      ]),

      this.section('축 보정', [
        this.toggle('Pitch 반전', 'invertPitch'),
        this.toggle('Yaw 반전', 'invertYaw'),
        this.toggle('Roll 반전', 'invertRoll'),
        this.slider('머리 강도', 'headGain', 0.4, 2.2, 0.05),
        this.slider('목 분배', 'neckShare', 0, 0.8, 0.05),
      ]),

      this.section('씬', [
        row([
          label('배경'),
          select(
            [
              ['none', '투명'],
              ['color', '색상'],
              ['image', '이미지'],
            ],
            'none',
            (value) => this.onBackgroundKind(value),
          ),
        ]),
        (() => {
          this.bgColorInput = document.createElement('input');
          this.bgColorInput.type = 'color';
          this.bgColorInput.value = '#14121c';
          this.bgColorInput.addEventListener('input', () => {
            this.sceneApi.setBackground({ type: 'color', color: this.bgColorInput!.value });
          });
          this.bgColorInput.style.display = 'none';
          return row([label('배경색'), this.bgColorInput]);
        })(),
        row([
          button('+ 텍스트', '', () => this.sceneApi.addText()),
          button('+ 이미지 파일', '', () => this.pickSceneImage()),
        ]),
        (this.inspector = el('div', 'inspector')),
        row([button('씬 링크 복사', 'primary wide', () => this.callbacks.onCopySceneLink())]),
        row([
          button('내보내기', '', () => this.exportScene()),
          button('가져오기', '', () => this.importScene()),
          button('초기화', '', () => this.sceneApi.clear()),
        ]),
        el('p', 'hint', [
          '아이템은 드래그로 이동, 클릭으로 선택 후 아래에서 편집. Delete로 삭제. ' +
            '씬 링크에는 배경·아이템이 전부 담기므로 OBS URL로도 그대로 재현됩니다.',
        ]),
      ]),

      this.section('OBS', [
        row([button('브라우저 소스 URL 복사', 'primary wide', () => this.callbacks.onCopyObsUrl())]),
        (() => {
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = initial.creditVisible;
          input.addEventListener('change', () =>
            this.callbacks.onCreditVisibilityChange(input.checked),
          );
          const wrapper = el('label', 'toggle');
          wrapper.append(input, document.createTextNode('라이선스 오버레이 표시'));
          return row([wrapper]);
        })(),
        el('p', 'hint', [
          'OBS → 소스 추가 → 브라우저 → URL 붙여넣기. 복사되는 URL에는 현재 모델이 ' +
            '포함되므로 OBS에서도 같은 아바타가 뜹니다.',
        ]),
      ]),

      this.section('디버그', [this.debugGrid()]),
    ]);

    root.append(this.dropzone, panel, this.licenseBox, this.errorBox);
  }

  setDropzoneVisible(visible: boolean): void {
    this.dropzone.classList.toggle('hidden', !visible);
  }

  setTrackingState(state: TrackingState): void {
    this.statusDot.classList.toggle('live', state === 'on');
    this.statusText.textContent =
      state === 'on' ? '추적 중' : state === 'starting' ? '준비 중…' : '정지';
    this.trackButton.textContent = state === 'off' ? '트래킹 시작' : '트래킹 정지';
    this.trackButton.disabled = state === 'starting';
  }

  setNotice(message: string): void {
    this.noticeBox.textContent = message;
  }

  /** Pushes config values back into the sliders after a programmatic change. */
  syncSliders(): void {
    for (const [key, { input, readout }] of this.sliders) {
      const raw = this.config[key];
      const shown = input.dataset['unit'] === 'deg' ? (raw * 180) / Math.PI : raw;
      input.value = shown.toFixed(2);
      readout.textContent = format(shown, input.dataset['unit'] === 'deg');
    }
  }

  setCreditVisible(visible: boolean): void {
    this.licenseBox.classList.toggle('suppressed', !visible);
  }

  setLicense(license: AvatarLicense | null): void {
    if (!license) {
      this.licenseBox.classList.remove('visible');
      return;
    }

    const author = license.authors.length > 0 ? license.authors.join(', ') : '작자 미상';
    const flags: string[] = [];
    if (license.licenseName) flags.push(license.licenseName);
    if (license.authorOnly) flags.push('저자 전용');
    if (license.commercialUsage) flags.push(`상업: ${license.commercialUsage}`);
    if (license.modification) flags.push(`개변: ${license.modification}`);

    this.licenseBox.innerHTML = '';
    this.licenseBox.append(
      el('div', '', [
        el('strong', '', [license.name ?? '이름 없는 모델']),
        ` — ${author}`,
        el('span', 'num', [`  VRM ${license.specVersion === '0' ? '0.x' : '1.0'}`]),
      ]),
    );
    if (flags.length > 0) {
      this.licenseBox.append(el('div', 'flag', [flags.join(' · ')]));
    }

    this.licenseBox.classList.toggle('required', license.creditRequired);
    this.licenseBox.classList.add('visible');
  }

  setDebug(debug: DriverDebug): void {
    const values: Record<string, string> = {
      tracked: debug.tracked ? 'yes' : 'no',
      pitch: deg(debug.pitch),
      yaw: deg(debug.yaw),
      roll: deg(debug.roll),
      blink: `${debug.blinkL.toFixed(2)} / ${debug.blinkR.toFixed(2)}`,
      mouth: debug.mouthOpen.toFixed(2),
      brow: `${debug.browUp.toFixed(2)} / ${debug.browDown.toFixed(2)}`,
      morphs: String(debug.browMorphs),
      rig: debug.rigBones > 0 ? `${debug.rigBones} bones` : '—',
      hands: debug.handBones > 0 ? `${debug.handBones} bones` : '—',
    };
    for (const [key, value] of Object.entries(values)) {
      const cell = this.debugCells.get(key);
      if (cell) cell.textContent = value;
    }
  }

  showError(message: string | null): void {
    this.errorBox.classList.toggle('hidden', message === null);
    this.errorBox.textContent = message ?? '';
  }

  private section(title: string, children: (Node | string)[]): HTMLElement {
    return el('section', '', [el('h2', '', [title]), ...children]);
  }

  private toggle(text: string, key: BooleanKey): HTMLElement {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = this.config[key];
    input.addEventListener('change', () => {
      this.callbacks.onConfigChange({ [key]: input.checked } as Partial<DriverConfig>);
    });

    const wrapper = el('label', 'toggle');
    wrapper.append(input, document.createTextNode(text));
    return row([wrapper]);
  }

  // ------------------------------------------------------------- scene UI

  private onBackgroundKind(kind: string): void {
    if (this.bgColorInput) this.bgColorInput.style.display = kind === 'color' ? '' : 'none';

    if (kind === 'none') {
      this.sceneApi.setBackground({ type: 'none' });
    } else if (kind === 'color') {
      this.sceneApi.setBackground({ type: 'color', color: this.bgColorInput?.value ?? '#14121c' });
    } else if (kind === 'image') {
      this.pickImageFile((url) => this.sceneApi.setBackground({ type: 'image', url }));
    }
  }

  private pickSceneImage(): void {
    this.pickImageFile((url) => this.sceneApi.addImage(url));
  }

  /**
   * Local image → data URL, embedded straight into the scene. Self-contained is
   * the point (the scene must survive into an OBS browser source that shares no
   * storage with this browser), but data URLs live inside the share link, so
   * size is capped to keep links usable.
   */
  private pickImageFile(done: (url: string) => void): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 300 * 1024) {
        this.showError('이미지가 300KB를 넘습니다 — 씬 링크가 너무 길어지므로 줄여주세요.');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') done(reader.result);
      };
      reader.readAsDataURL(file);
    });
    input.click();
  }

  renderInspector(item: SceneItem | null): void {
    const box = this.inspector;
    if (!box) return;
    box.innerHTML = '';
    if (!item) return;

    if (item.kind === 'text') {
      const textInput = document.createElement('input');
      textInput.type = 'text';
      textInput.className = 'text-input';
      textInput.value = item.text;
      textInput.addEventListener('input', () => {
        this.sceneApi.updateItem(item.id, { text: textInput.value });
      });
      box.append(row([textInput]));

      const sizeInput = document.createElement('input');
      sizeInput.type = 'range';
      sizeInput.min = '10';
      sizeInput.max = '160';
      sizeInput.value = String(item.size);
      sizeInput.addEventListener('input', () => {
        this.sceneApi.updateItem(item.id, { size: Number(sizeInput.value) });
      });
      box.append(row([label('크기'), sizeInput]));

      const colorInput = document.createElement('input');
      colorInput.type = 'color';
      colorInput.value = item.color;
      colorInput.addEventListener('input', () => {
        this.sceneApi.updateItem(item.id, { color: colorInput.value });
      });

      const boldToggle = document.createElement('input');
      boldToggle.type = 'checkbox';
      boldToggle.checked = item.bold;
      boldToggle.addEventListener('change', () => {
        this.sceneApi.updateItem(item.id, { bold: boldToggle.checked });
      });
      const boldLabel = el('label', 'toggle');
      boldLabel.append(boldToggle, document.createTextNode('굵게'));

      box.append(row([label('색'), colorInput, boldLabel]));
    } else {
      const widthInput = document.createElement('input');
      widthInput.type = 'range';
      widthInput.min = '4';
      widthInput.max = '100';
      widthInput.value = String(item.width);
      widthInput.addEventListener('input', () => {
        this.sceneApi.updateItem(item.id, { width: Number(widthInput.value) });
      });
      box.append(row([label('너비'), widthInput]));
    }

    box.append(row([button('아이템 삭제', 'wide', () => this.sceneApi.removeItem(item.id))]));
  }

  private exportScene(): void {
    const blob = new Blob([this.sceneApi.exportJson()], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'vrm-stage-scene.json';
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private importScene(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      void file.text().then((json) => {
        if (!this.sceneApi.importJson(json)) {
          this.showError('씬 파일을 읽을 수 없습니다 — 형식을 확인해주세요.');
        }
      });
    });
    input.click();
  }

  setActiveEmotion(name: string | null): void {
    for (const [emotion, chip] of this.emotionChips) {
      chip.classList.toggle('on', emotion === name);
    }
  }

  private handToggle(text: string, key: 'fingers' | 'arms' | 'invertCurl' | 'wrist'): HTMLElement {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = this.handConfig[key];
    input.addEventListener('change', () => {
      this.callbacks.onHandConfigChange({ [key]: input.checked } as Partial<HandRigConfig>);
    });

    const wrapper = el('label', 'toggle');
    wrapper.append(input, document.createTextNode(text));
    return row([wrapper]);
  }

  private handSlider(
    text: string,
    key: 'armReach',
    min: number,
    max: number,
    step: number,
  ): HTMLElement {
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(this.handConfig[key]);

    const readout = el('span', 'num', [input.value]);
    input.addEventListener('input', () => {
      readout.textContent = Number(input.value).toFixed(2);
      this.callbacks.onHandConfigChange({ [key]: Number(input.value) } as Partial<HandRigConfig>);
    });

    const right = el('span', 'status');
    right.append(input, readout);
    return row([label(text), right]);
  }

  private slider(
    text: string,
    key: NumericKey,
    min: number,
    max: number,
    step: number,
    unit: 'raw' | 'deg' = 'raw',
  ): HTMLElement {
    const isDegrees = unit === 'deg';
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.dataset['unit'] = unit;

    const initial = isDegrees ? (this.config[key] * 180) / Math.PI : this.config[key];
    input.value = String(initial);

    const readout = el('span', 'num', [format(initial, isDegrees)]);
    input.addEventListener('input', () => {
      const shown = Number(input.value);
      readout.textContent = format(shown, isDegrees);
      const stored = isDegrees ? (shown * Math.PI) / 180 : shown;
      this.callbacks.onConfigChange({ [key]: stored } as Partial<DriverConfig>);
    });

    this.sliders.set(key, { input, readout });

    const right = el('span', 'status');
    right.append(input, readout);
    return row([label(text), right]);
  }

  private debugGrid(): HTMLElement {
    const grid = el('div', 'debug');
    for (const [key, caption] of [
      ['tracked', 'tracked'],
      ['pitch', 'pitch'],
      ['yaw', 'yaw'],
      ['roll', 'roll'],
      ['blink', 'blink L/R'],
      ['mouth', 'mouth'],
      ['brow', 'brow up/dn'],
      ['morphs', 'brow morphs'],
      ['rig', 'rig source'],
      ['hands', 'hand rig'],
    ] as const) {
      const value = el('b', '', ['—']);
      this.debugCells.set(key, value);
      grid.append(el('span', '', [caption]), value);
    }
    return grid;
  }
}

function el(tag: string, className = '', children: (Node | string)[] = []): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  for (const child of children) {
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

function row(children: (Node | string)[]): HTMLElement {
  return el('div', 'row', children);
}

function label(text: string): HTMLElement {
  return el('span', '', [text]);
}

function button(text: string, className: string, onClick: () => void): HTMLButtonElement {
  const node = document.createElement('button');
  node.textContent = text;
  if (className) node.className = className;
  node.addEventListener('click', onClick);
  return node;
}

function select(
  options: [string, string][],
  initial: string,
  onChange: (value: string) => void,
): HTMLSelectElement {
  const node = document.createElement('select');
  for (const [value, text] of options) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = text;
    node.append(option);
  }
  node.value = initial;
  node.addEventListener('change', () => onChange(node.value));
  return node;
}

function format(value: number, isDegrees: boolean): string {
  return isDegrees ? `${value.toFixed(1)}°` : value.toFixed(2);
}

function deg(radians: number): string {
  return `${((radians * 180) / Math.PI).toFixed(1)}°`;
}
